import { useEffect, useState, useCallback } from "react";
import { Title } from "react-admin";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { API, fetchJson } from "../../api";

interface StatsRow {
  operatorId: string;
  currency: string;
  sumBets: number;
  sumRollbacks: number;
  netBets: number;
  sumPayouts: number;
  ggr: number;
  rtpPercent: number;
  betCount: number;
  rollbackCount: number;
  winCount: number;
  winRatePercent: number;
}

type Range = "today" | "7d" | "30d" | "all";

function rangeToParams(range: Range): string {
  if (range === "all") return "";
  const now = Date.now();
  const ms: Record<string, number> = { today: 86400000, "7d": 604800000, "30d": 2592000000 };
  return `?from=${now - ms[range]}&to=${now}`;
}

function fmt(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const OperatorStatsPage = () => {
  const [rows, setRows] = useState<StatsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<Range>("all");

  const load = useCallback(async (r: Range) => {
    setLoading(true);
    try {
      const data = await fetchJson<StatsRow[]>(`${API}/db/operators/stats${rangeToParams(r)}`);
      setRows(data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(range);
  }, [range, load]);

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Operator Stats" />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#e2e8f0", letterSpacing: -0.3 }}>
          Operator Stats
        </Typography>
        <ToggleButtonGroup
          value={range}
          exclusive
          onChange={(_, v) => v && setRange(v as Range)}
          size="small"
        >
          <ToggleButton value="today" sx={{ fontSize: "0.7rem", px: 1.5 }}>Today</ToggleButton>
          <ToggleButton value="7d" sx={{ fontSize: "0.7rem", px: 1.5 }}>7d</ToggleButton>
          <ToggleButton value="30d" sx={{ fontSize: "0.7rem", px: 1.5 }}>30d</ToggleButton>
          <ToggleButton value="all" sx={{ fontSize: "0.7rem", px: 1.5 }}>All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#94a3b8" }}>
          No data for selected period.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Operator</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell align="right">Bets</TableCell>
              <TableCell align="right">Rollbacks</TableCell>
              <TableCell align="right">Net Bets</TableCell>
              <TableCell align="right">Payouts</TableCell>
              <TableCell align="right">GGR</TableCell>
              <TableCell align="right">RTP %</TableCell>
              <TableCell align="right">Bet Count</TableCell>
              <TableCell align="right">Win Count</TableCell>
              <TableCell align="right">Win Rate %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={`${r.operatorId}-${r.currency}`}>
                <TableCell sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.8rem" }}>{r.operatorId}</TableCell>
                <TableCell>{r.currency}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(r.sumBets)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", color: "#94a3b8" }}>{fmt(r.sumRollbacks)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(r.netBets)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(r.sumPayouts)}</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: r.ggr >= 0 ? "#34d399" : "#f87171" }}
                >
                  {fmt(r.ggr)}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.rtpPercent.toFixed(2)}</TableCell>
                <TableCell align="right">{r.betCount}</TableCell>
                <TableCell align="right">{r.winCount}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.winRatePercent.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
